<template>
  <div>
    <div v-if="qrDialogCamera">
      <qrcode-stream-koee
        @codeScanned="codeArrived"
        @error="errorCamera"
        class="qrcode-stream-koee"
      ></qrcode-stream-koee>
    </div>
    <div class="ar-sensor" v-else>
      <div class="content-target-found" ref="contentTargetFound">
        <div class="inform-block">
          <div class="top-inform" v-if="!isButtonPress">
            <div class="top-block">
              <div class="inform-left-block">
                <svg-icon
                  size="22"
                  :name="sensorIcon(sensorCode)"
                  class="fill-icon mr-2 icon-light mb-2"
                ></svg-icon>
                <p class="mb-0 height-name">{{ sensorName(sensorCode) }}</p>
                <span>{{ sensorValue }} {{ sensorUnits(sensorCode) }}</span>
              </div>
              <div class="inform-right-block">
                <svg-icon
                  size="22"
                  :name="sensorIcon(sensorCode)"
                  class="fill-icon mr-2 icon-light mb-2"
                ></svg-icon>
                <p class="mb-0 height-name">{{ averageSensorName(sensorCode) }}</p>
                <span>{{ sensorAvg }} {{ sensorUnits(sensorCode) }}</span>
              </div>
            </div>
          </div>
          <div class="bottom-inform">
            <div class="bottom-block">
              <div class="inform-left-block">
                <svg-icon
                  size="22"
                  name="bar-chart"
                  class="fill-icon mr-2 icon-light mb-2"
                ></svg-icon>
                <p class="mb-0 height-name">{{ $t('sensorAr.signalLevel') }}</p>
                <span>{{ sensorSignal }} %</span>
              </div>
              <div class="inform-right-block">
                <svg-icon
                  size="22"
                  name="battery"
                  fill="#ffffff"
                  class="fill-icon mr-2 icon-light mb-2"
                ></svg-icon>
                <p class="mb-0 height-name">{{ $t('sensorAr.batteryCharge') }}</p>
                <span>{{ sensorBattery }} %</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <a-scene
        mindar-image="imageTargetSrc: ../../models/sensor-model.mind;"
        vr-mode-ui="enabled: false"
        device-orientation-permission-ui="enabled: false"
      >
        <a-camera position="0 0 0" look-controls="enabled: false"></a-camera>
        <a-entity mindar-image-target="targetIndex: 0" ref="targetFound"></a-entity>
      </a-scene>
    </div>
  </div>
</template>

<script>
import 'aframe';
import 'mind-ar/dist/mindar-image.prod.js';
import 'mind-ar/dist/mindar-image-aframe.prod.js';

import IiotDataClient from '@/api/iiot-data/client.js';
import { SENSOR_ID, AR_SENSOR, SENSOR_UNITS } from '@/utils/const.js';
import sensorByGuid from '@/api/anonymous/sensors/sensorByGuid.gql';

export default {
  name: 'SensorAr',
  data() {
    return {
      qrDialogCamera: true,
      iiotDataClient: {},
      sensorValue: 0,
      sensorBattery: 0,
      sensorSignal: 0,
      sensorAvg: 0,
      sensorCode: '',
    };
  },
  components: {
    QrcodeStreamKoee: () => import('@/components/qrcode/QrcodeStream'),
  },
  computed: {
    isButtonPress() {
      return AR_SENSOR.BUTTON_PRESS == this.sensorCode;
    },
  },
  methods: {
    sensorName(sensorCode) {
      return this.$t(`sensorAr.${sensorCode || 'STMP'}`);
    },
    averageSensorName(sensorCode) {
      switch (sensorCode) {
        case AR_SENSOR.TEMPERATURE:
          return this.$t('sensorAr.averageTemperature');
        case AR_SENSOR.VIBRATION:
          return this.$t('sensorAr.averageVibration');
        case AR_SENSOR.PRESSURE:
          return this.$t('sensorAr.averagePressure');
        default:
          return this.$t('sensorAr.averageTemperature');
      }
    },
    sensorUnits(sensorCode) {
      switch (sensorCode) {
        case AR_SENSOR.TEMPERATURE:
          return SENSOR_UNITS.TEMPERATURE;
        case AR_SENSOR.VIBRATION:
          return SENSOR_UNITS.VIBRATION;
        case AR_SENSOR.PRESSURE:
          return SENSOR_UNITS.PRESSURE;
        default:
          return SENSOR_UNITS.TEMPERATURE;
      }
    },
    sensorIcon(sensorCode) {
      switch (sensorCode) {
        case AR_SENSOR.TEMPERATURE:
          return 'thermometer';
        case AR_SENSOR.VIBRATION:
          return 'vibration';
        case AR_SENSOR.PRESSURE:
          return 'pressure';
        default:
          return 'thermometer';
      }
    },
    errorCamera(error) {
      this.showModal(error);
    },
    showModal(e) {
      this.$bvToast.toast(e, {
        title: this.$t('toast.error'),
        variant: 'danger',
        solid: true,
      });
    },
    codeArrived(str, isGuid) {
      let guid;
      if (isGuid) {
        guid = str;
      } else {
        const urlList = str.split('/');
        guid = urlList.pop();
      }

      if (!guid) {
        this.showModal(this.$t('addBox.toast.qrNotValid'));
        return;
      }
      this.sensorCodeQuery(guid);
      this.subscribeToSensor(guid);
      this.qrDialogCamera = false;
      this.foundArImage();
    },
    sensorCodeQuery(guid) {
      this.$apollo
        .query({
          query: sensorByGuid,
          variables: {
            guid,
          },
        })
        .then((d) => {
          const queryData = d[Object.keys(d)[0]];
          let sensorData = queryData.koeeBoxAnonymousQuery.sensorByGuid;
          this.sensorCode = sensorData?.sensorTypeId || '';
        });
    },
    subscribeToSensor(guid) {
      this.iiotDataClient = new IiotDataClient(guid);

      this.iiotDataClient.subscribeToSensor(SENSOR_ID.VALUE, (v) => {
        this.sensorValue = v;
      });
      this.iiotDataClient.subscribeToSensor(SENSOR_ID.BATTERY, (v) => {
        this.sensorBattery = v;
      });
      this.iiotDataClient.subscribeToSensor(SENSOR_ID.SIGNAL, (v) => {
        this.sensorSignal = v;
      });
      this.iiotDataClient.subscribeToSensor(SENSOR_ID.AVG, (v) => {
        this.sensorAvg = v;
      });
    },
    unsubscribeFromSensor() {
      if (this.iiotDataClient) {
        this.iiotDataClient.unsubscribeFromSensor(SENSOR_ID.VALUE);
        this.iiotDataClient.unsubscribeFromSensor(SENSOR_ID.BATTERY);
        this.iiotDataClient.unsubscribeFromSensor(SENSOR_ID.SIGNAL);
        this.iiotDataClient.unsubscribeFromSensor(SENSOR_ID.AVG);
        this.iiotDataClient.closeConnect();
      }
    },
    foundArImage() {
      this.$nextTick().then(() => {
        this.$refs.targetFound.addEventListener('targetFound', () => {
          this.$refs.contentTargetFound.style.display = 'block';
        });
        this.lostArImage();
      });
    },
    lostArImage() {
      this.$nextTick().then(() => {
        this.$refs.targetFound.addEventListener('targetLost', () => {
          this.$refs.contentTargetFound.style.display = 'none';
          this.qrDialogCamera = true;
          this.unsubscribeFromSensor();
        });
      });
      this.sensorValue = 0;
      this.sensorBattery = 0;
      this.sensorSignal = 0;
      this.sensorAvg = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.qrcode-stream-koee :deep {
  .qrcode-stream-camera {
    position: fixed;
    right: 0;
    bottom: 0;
    min-width: 100%;
    min-height: 100%;
    width: auto;
    height: auto;
    background-size: cover;
    max-width: inherit !important;
    max-height: inherit !important;
  }

  .qrcode-stream-wrapper {
    position: inherit !important;
    max-width: inherit !important;
    max-height: inherit !important;
    z-index: inherit !important;
  }
}
.ar-sensor :deep {
  position: relative;
  .height-name {
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .content-target-found {
    position: absolute;
    z-index: 9;
    display: none;
    top: 10%;
    bottom: 10%;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    .bottom-inform {
      bottom: 0;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }
    .inform-block {
      position: relative;
      height: 100%;
      .top-block,
      .bottom-block {
        &::before {
          content: '';
          background-color: #191c24;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          opacity: 0.7;
          position: absolute;
          border-radius: 20px;
          z-index: -1;
        }
        width: 350px;
        position: relative;
        display: flex;
        justify-content: space-around;
        padding: 10px;
        box-shadow: -4px 4px 4px #1e2129, 0px -2px 4px #14171d;
        border-radius: 20px;
        top: 0;

        .inform-left-block {
          background-color: #191c24;
          padding: 10px 30px;
          text-align: center;
          border-radius: 20px;
          max-width: 150px;
        }
        .inform-right-block {
          background-color: #191c24;
          padding: 10px 30px;
          text-align: center;
          border-radius: 20px;
          max-width: 150px;
        }
      }
      .bottom-block {
        position: relative;
        bottom: 0px;
      }
    }
  }
  video {
    z-index: 1 !important;
    top: 0 !important;
    bottom: 0 !important;
  }
  .a-canvas {
    position: relative !important;
    z-index: 2 !important;
  }
  a-sky {
    display: block;
    position: relative;
    z-index: 2;
  }
  .inform {
    background: #191c24;
    box-shadow: -4px -4px 4px #1e2129, 4px 4px 4px #14171d;
  }
}
@media screen and (orientation: landscape) {
  .ar-sensor :deep {
    position: relative;
    .content-target-found {
      position: absolute;
      display: none;
      top: 44%;
      left: 10%;
      right: 10%;
      transform: translateY(-50%);
      .bottom-inform {
        position: static;
      }
      .inform-block {
        position: relative;
        height: 100%;
        display: flex;
        justify-content: space-between;
        .top-block,
        .bottom-block {
          width: 150px;
          position: relative;
          display: block;
          justify-content: space-around;

          .inform-left-block {
            margin-bottom: 15px;
            padding: 15px 0;
          }
          .inform-right-block {
            padding: 15px 0;
          }
        }
      }
    }
  }
}
</style>
